import React, { FC, ReactNode } from "react";
import parse from "html-react-parser";

import { PRODUCT_INDEX_EN } from "@/constants/algolia";
import { FormatSubType, FormatType } from "@/constants/tagular/main";
import useProductEvents from "@/hooks/eventing/useProductEvents";
import { autocompleteSuggestionClickEvent } from "@/lib/segment";
import convertUrlToRelative from "@/utils/convertUrlToRelative";
import { translateVariant } from "@/utils/tagular/dataTranslationMatrices";
import {
  getBrandStringFromPartnerKeys,
  getTagularSubject,
  setCorrelationId,
} from "@/utils/tagular/helpers";
import useMarketplaceAttributionHref from "@/hooks/useMarketplaceAttributionHref";

import {
  AlgoliaHit,
  SearchResultWithHighlight,
  ProductSummaryWithHighlight,
} from "./types";

interface ProductListItemProps {
  hit: AlgoliaHit;
  location?: string;
}

type HitLinkProps = {
  hit: AlgoliaHit;
  indexName: string;
  queryId?: string;
  location?: string;
  children: ReactNode;
};

const HitLink: FC<HitLinkProps> = ({
  hit,
  indexName,
  queryId,
  location = "embedded-search",
  children,
}) => {
  const position = hit.__position || 0;

  // Conditionally extract fields based on index type
  const isProductIndex = PRODUCT_INDEX_EN === "product";
  const title = isProductIndex
    ? (hit as SearchResultWithHighlight).title
    : (hit as ProductSummaryWithHighlight).productName;
  const partnerKeys = isProductIndex
    ? (hit as SearchResultWithHighlight).partner_keys
    : (hit as ProductSummaryWithHighlight).partnerKeys || [];
  const subject = isProductIndex
    ? (hit as SearchResultWithHighlight).subject
    : (hit as ProductSummaryWithHighlight).attributes || [];
  const uuid = isProductIndex
    ? (hit as SearchResultWithHighlight).uuid
    : (hit as ProductSummaryWithHighlight).productUuid;
  const marketingUrl = isProductIndex
    ? (hit as SearchResultWithHighlight).marketing_url
    : (hit as ProductSummaryWithHighlight).productSlug;
  const product = hit.product || "";

  const productData = {
    brand: getBrandStringFromPartnerKeys(partnerKeys),
    formatType: FormatType.Search,
    formatSubtype: FormatSubType.SearchResult,
    location,
    position: position,
    category: getTagularSubject(subject),
    name: title,
    variant: translateVariant(product),
    productId: uuid,
  };

  const { productClicked, correlationId } = useProductEvents({
    product: productData,
  });

  const href = marketingUrl ? `/${convertUrlToRelative(marketingUrl)}` : "#";
  const attributedHref = useMarketplaceAttributionHref(href);

  const handleClick = () => {
    setCorrelationId(correlationId);

    productClicked();

    const objectID = isProductIndex
      ? (hit as SearchResultWithHighlight).objectID
      : (hit as ProductSummaryWithHighlight).objectID || "";

    autocompleteSuggestionClickEvent({
      link: attributedHref,
      objectId: objectID,
      uuid: uuid,
      product: product,
      queryId,
      indexName,
    });
  };

  return (
    <a
      className="no-underline hover:no-underline flex items-center"
      onClick={handleClick}
      key={uuid}
      href={attributedHref}
    >
      {children}
    </a>
  );
};

/**
 * Renders a search hit component.
 *
 * @param hit - The search hit object.
 * @returns The rendered search hit component.
 */
const ProductListItem = ({ hit, location }: ProductListItemProps) => {
  const isProductIndex = PRODUCT_INDEX_EN === "product";
  const partnerKeys = isProductIndex
    ? (hit as SearchResultWithHighlight).partner_keys
    : (hit as ProductSummaryWithHighlight).partnerKeys || [];
  const programType = isProductIndex
    ? ((hit as SearchResultWithHighlight).program_type as string[])
    : [(hit as ProductSummaryWithHighlight).productType];
  const product = hit.product || "";
  const queryID = isProductIndex
    ? (hit as SearchResultWithHighlight).__queryID
    : undefined;

  // Properly typed _highlightResult access based on index type
  const highlightedTitle = isProductIndex
    ? (hit as SearchResultWithHighlight)._highlightResult.title.value
    : (hit as ProductSummaryWithHighlight)._highlightResult.productName.value;

  return (
    <HitLink
      hit={hit}
      location={location}
      indexName={PRODUCT_INDEX_EN}
      queryId={queryID}
    >
      <div className="flex flex-wrap gap-1 hover:no-underline">
        <span>{parse(highlightedTitle)}</span>
        <span className="bg-putty-light rounded text-xs text-black py-0.5 px-2 hover:no-underline">
          {partnerKeys[0]}
        </span>
        <span className="bg-primary-500 text-white rounded text-xs py-0.5 px-2 hover:no-underline">
          {programType.length > 0 && programType[0].includes("Masters")
            ? "Masters"
            : product === "OCM"
              ? "Course"
              : product}
        </span>
      </div>
    </HitLink>
  );
};

export default ProductListItem;
